import { Box, Divider, Typography } from '@mui/material';
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { IUserSportBackgroundResponse } from '../../interfaces/user-sport-background.interface';
import SectionLayout from '../../layouts/SectionLayout';
import EditSportBackground from '../../pages/auth/ProfilePage/sport-background/EditSportBackground';
import userApi from '../../store/apiSlice/userApi';
import SportBackground from './Forms/SportBackgroundForm/SportBackground';
import { useNavigate } from 'react-router-dom';

interface SportBackgroundsSectionProps {
  sportBackgrounds?: IUserSportBackgroundResponse[];
}

const SportBackgroundsSection = ({
  sportBackgrounds,
}: SportBackgroundsSectionProps) => {
  const sportBgRefs = useRef<
    { sportBgId: string; element: HTMLDivElement | null }[]
  >([]);
  const editSportBgRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [editSportBackground, setEditSportBackground] =
    useState<IUserSportBackgroundResponse | null>(null);

  const sortedBgs = useMemo(() => {
    if (!sportBackgrounds) {
      return [];
    }

    return [...sportBackgrounds].sort(
      (a, b) =>
        new Date(b.startCareerDate).getTime() -
        new Date(a.startCareerDate).getTime()
    );
  }, [sportBackgrounds]);

  useEffect(() => {
    if (sportBackgrounds && sportBackgrounds.length === 0)
      navigate('/welcome', { replace: true });
  }, [sportBackgrounds, navigate]);

  useEffect(() => {
    if (editSportBackground) {
      setTimeout(() => {
        if (editSportBgRef.current) {
          editSportBgRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 500);
    }
  }, [editSportBackground]);

  const openSportBackgroundEdit = useCallback(
    (sportBg: IUserSportBackgroundResponse | null) => () => {
      setEditSportBackground(sportBg);
    },
    []
  );

  const closeSportBackgroundEdit = useCallback(
    (sportBgId: string) => {
      // Ensure unified /me data gets refreshed after edits on /profile.
      dispatch(userApi.util.invalidateTags(['user']));
      openSportBackgroundEdit(null)();

      setTimeout(() => {
        if (sportBgRefs.current.length) {
          sportBgRefs.current
            .find((el) => el.sportBgId === sportBgId)
            ?.element?.scrollIntoView({ behavior: 'smooth' });
        }
      }, 500);
    },
    [dispatch, openSportBackgroundEdit]
  );

  return (
    <SectionLayout>
      <Typography component="h2" variant="h2" mb={6}>
        Sport background
        {sportBackgrounds && sportBackgrounds?.length > 1 && 's'}
      </Typography>
      <Box display="flex" flexDirection="column" rowGap={6}>
        {!editSportBackground &&
          !!sportBackgrounds?.length &&
          sortedBgs.map((sportBg, index) => (
            <Fragment key={sportBg.id}>
              <SportBackground
                ref={(el) =>
                  (sportBgRefs.current[index] = {
                    sportBgId: sportBg.id,
                    element: el,
                  })
                }
                sportBg={sportBg}
                openSportBackgroundEdit={openSportBackgroundEdit(sportBg)}
              />
              {index !== sportBackgrounds.length - 1 && <Divider />}
            </Fragment>
          ))}
      </Box>

      {editSportBackground && (
        <EditSportBackground
          ref={editSportBgRef}
          sportBg={editSportBackground}
          openSportBackgroundEditClose={closeSportBackgroundEdit}
        />
      )}
    </SectionLayout>
  );
};

export default SportBackgroundsSection;
